import { format, isValid, parse } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CalendarDays } from 'lucide-react';
import * as React from 'react';
import { cn } from '@/lib/utils';
import { Calendar } from './calendar';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

interface DatePickerInputProps {
  label?: string;
  value?: Date;
  onChange?: (date: Date | undefined) => void;
  placeholder?: string;
  className?: string;
}

function applyDateMask(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

export function DatePickerInput({
  label,
  value,
  onChange,
  placeholder = 'dd/mm/aaaa',
  className,
}: DatePickerInputProps) {
  const [open, setOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState(() =>
    value && isValid(value) ? format(value, 'dd/MM/yyyy') : '',
  );

  React.useEffect(() => {
    if (value && isValid(value)) {
      setInputValue(format(value, 'dd/MM/yyyy'));
    } else if (!value) {
      setInputValue('');
    }
  }, [value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const masked = applyDateMask(e.target.value);
    setInputValue(masked);

    if (masked.length === 10) {
      const parsed = parse(masked, 'dd/MM/yyyy', new Date());
      if (isValid(parsed)) {
        onChange?.(parsed);
      }
    } else if (masked.length === 0) {
      onChange?.(undefined);
    }
  };

  const handleCalendarSelect = (date: Date | undefined) => {
    if (date && isValid(date)) {
      onChange?.(date);
      setInputValue(format(date, 'dd/MM/yyyy'));
    }
    setOpen(false);
  };

  return (
    <div className={cn('w-full', className)}>
      {label && <span className="block text-sm font-medium text-gray-700 mb-2">{label}</span>}
      <Popover open={open} onOpenChange={setOpen}>
        <div className="relative">
          <input
            type="text"
            inputMode="numeric"
            placeholder={placeholder}
            value={inputValue}
            onChange={handleInputChange}
            className="flex h-9 w-full rounded-md border border-gray-300 bg-transparent px-3 py-1 pr-9 text-sm shadow-sm transition-colors placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-500"
          />
          <PopoverTrigger asChild>
            <button
              type="button"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <CalendarDays size={16} />
            </button>
          </PopoverTrigger>
        </div>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={value}
            onSelect={handleCalendarSelect}
            locale={ptBR}
            defaultMonth={value}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
